import { DEFAULT_BORDER } from '../borderStyles';
import { sharedDefaults } from '../../theme/styleSchema';

/**
 * Schéma de style de la Carte info (cf. shared/theme/styleSchema) : ses
 * réglages, côté document comme côté site. Le document et le site les rangent
 * aujourd'hui sous les mêmes noms ; un réglage qui divergerait porterait sa
 * conversion ici (`classic`), comme ceux du Tableau.
 *
 * Hors schéma, propres au bloc du site : le CONTENU texte (titre et
 * description, en Draft.js dans le document, en HTML sur le site) et les
 * ALIGNEMENTS de la box dans sa colonne (le document les modélise déjà par
 * `box.positions`).
 *
 * Défauts en fonctions : `border` et `badges` sont des objets, chaque carte a
 * les siens.
 */
export const INFO_CARD_STYLE_SCHEMA = {
  // Posés par un style de la galerie comme du thème.
  titleStyle: { default: 'figure' },
  illustration: { default: 'icon', seed: true },
  illustrationShape: { default: 'round' },
  illustrationSize: { default: '3.25rem' },
  cardLayout: { default: 'vertical' },
  contentAlign: { default: 'center' },
  // Longueurs CSS (px affichés dans les champs, stockage rem).
  lineGap: { default: '0.625rem' },
  backgroundColor: { default: '' },
  border: { default: () => ({ ...DEFAULT_BORDER }) },
  quoteMark: { default: false },
  rating: { default: 0, seed: true },

  // Posés par un style du thème seulement.
  color: { style: 'theme', default: '' },
  /** Couleur des étoiles. Vide = celle de l'illustration. */
  ratingColor: { style: 'theme', default: '' },
  /** Taille d'une étoile (longueur CSS, champ px↔rem). */
  ratingSize: { style: 'theme', default: '1rem' },
  logoHeight: { style: 'theme', default: '2rem' },
  /** Clé de l'échelle d'ombre (`shared/boxes/shadowStyles`). */
  shadow: { style: 'theme', default: 'none' },
  alignHorizontal: { style: 'theme', seed: true, only: 'site' },
  alignVertical: { style: 'theme', seed: true, only: 'site' },
  /** Style par défaut des textes, par rôle (`value`, `description`). */
  textStyles: { style: 'theme', only: 'site' },

  // Contenu commun : recopié d'un monde à l'autre, jamais posé par un style —
  // basculer d'un style à l'autre pour voir effacerait le travail de
  // l'utilisateur.
  icon: { style: false, default: 'IconStar' },
  imageUrl: { style: false, default: '' },
  logoUrl: { style: false, default: '' },
  badges: { style: false, default: () => [] },

  // Document seul : la couleur de départ des deux textes Draft.js. Sur le
  // site, c'est le HTML produit qui porte les couleurs.
  titleColor: { style: false, only: 'document', default: '' },
  descriptionColor: { style: false, only: 'document', default: '' },
};

/** Props de la Carte info communes au document et au site. */
export function infoCardSharedProps() {
  return sharedDefaults(INFO_CARD_STYLE_SCHEMA);
}

/**
 * Props par défaut du BLOC WEB : les communes, plus son contenu et ses
 * alignements. SOURCE UNIQUE de sa config Puck comme des macros qui en posent
 * (Chiffres clés, Équipe) — une carte créée par macro doit porter toutes ses
 * props, sinon les champs de la sidebar s'affichent vides alors que le rendu
 * applique bien les défauts.
 */
export function infoCardDefaultProps() {
  return {
    value: '',
    description: '',
    ...infoCardSharedProps(),
    alignHorizontal: 'center',
    alignVertical: 'center',
  };
}

/**
 * Props d'une carte complétées par les défauts. À passer devant TOUTE lecture
 * qui compare : une carte enregistrée avant l'ajout d'une prop ne la porte pas
 * (README webviewer §4), et sans ce complément elle ne correspondrait à aucun
 * style — toutes les cartes déjà en base s'afficheraient « Personnalisé ».
 */
export function normalizeInfoCardProps(props) {
  return { ...infoCardDefaultProps(), ...(props || {}) };
}

/**
 * Le style d'une carte dans une liste de styles de Carte info, ou le style par
 * défaut si l'identifiant n'y est pas — cas d'une carte venue d'une section
 * liée dont la configuration n'a pas été reprise.
 */
export function resolveInfoCardStyle(styles, styleId) {
  const list = styles || [];
  return list.find((style) => style.id === styleId) ?? list[0] ?? null;
}
